import { useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Download, Eye, Trash2 } from 'lucide-react';
import {
  Badge,
  type BadgeTone,
  Button,
  ConfirmDialog,
  DataTable,
  type DataTableColumn,
  type DataTableSort,
  EmptyState,
  Input,
  PageBody,
  TableActionsMenu,
} from '@wundo/ui';
import { formatFileSize } from '@wundo/shared/fileSize.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { DocumentChunksDialog } from '@/components/documents/DocumentChunksDialog.tsx';
import { UploadQueue, type UploadItem } from '@/components/documents/UploadQueue.tsx';
import {
  deleteDocument,
  getDocumentDownloadUrl,
  listDocuments,
  uploadDocument,
} from '@/lib/documents.ts';
import { useActiveOrganisation } from '@/lib/activeOrganisation.ts';
import { formatDateTime } from '@/utils/dateUtils.ts';
import type { Document, DocumentStatus } from '@/types/documents.ts';
import { reportError } from '@/lib/reportError.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const Route = createFileRoute('/_app/documents')({
  staticData: { orgScope: 'required' },
  component: DocumentsPage,
});

const STATUS_LABEL: Record<DocumentStatus, string> = {
  pending: 'Pending',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
};

const STATUS_TONE: Record<DocumentStatus, BadgeTone> = {
  pending: 'neutral',
  processing: 'info',
  completed: 'success',
  failed: 'danger',
};

const columns: DataTableColumn<Document>[] = [
  { key: 'file_name', header: 'File name', sortable: true },
  {
    key: 'file_size',
    header: 'Size',
    sortable: true,
    render: (row) => formatFileSize(row.file_size),
  },
  {
    key: 'status',
    header: 'Status',
    sortable: true,
    render: (row) => (
      <Badge tone={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</Badge>
    ),
  },
  {
    key: 'created_at',
    header: 'Created at',
    sortable: true,
    render: (row) => formatDateTime(row.created_at),
  },
];

/**
 * Hand the browser a file it fetches itself. The URL is a short-lived link to
 * object storage, so this is a navigation rather than a download of our own —
 * the anchor is only there to carry the filename.
 */
function startDownload(url: string, fileName: string): void {
  const link = window.document.createElement('a');
  link.href = url;
  link.download = fileName;
  window.document.body.appendChild(link);
  link.click();
  link.remove();
}

/** Every document an organisation has given Wundo to read; uploading is the point, the table the receipt. */
export function DocumentsPage() {
  const { organisation } = useActiveOrganisation();
  const organisationId = organisation?.id;

  const upload = useDocumentUpload(organisationId);
  const [deleting, setDeleting] = useState<Document | null>(null);
  const [viewing, setViewing] = useState<Document | null>(null);
  const [downloadError, setDownloadError] = useState('');
  const field = useRef<HTMLInputElement>(null);

  const { documents, page, setPage, sort, changeSort } = useDocumentList(organisationId);

  async function download(row: Document) {
    setDownloadError('');
    try {
      startDownload(await getDocumentDownloadUrl(row.id), row.file_name);
    } catch (failure) {
      reportError(failure, { where: 'documents.download', documentId: row.id });
      setDownloadError('Failed to download file');
    }
  }

  const rows = documents.data?.data ?? [];
  const meta = documents.data?.meta;

  return (
    <>
      <PageHeader title='Documents' />
      <PageBody>
        {upload.error !== '' && (
          <p
            role='alert'
            className='mb-3.5 rounded-md bg-danger-fill px-3.5 py-2.5 text-sm font-semibold text-danger'
          >
            {upload.error}
          </p>
        )}

        {downloadError !== '' && (
          <p
            role='alert'
            className='mb-3.5 rounded-md bg-danger-fill px-3.5 py-2.5 text-sm font-semibold text-danger'
          >
            {downloadError}
          </p>
        )}

        <UploadQueue items={upload.uploads} />

        <DataTable
          label='Documents'
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          loading={documents.isLoading}
          error={documents.isError ? 'Failed to load documents' : undefined}
          sort={sort}
          onSortChange={changeSort}
          pagination={{ page, pageSize: PAGE_SIZE, total: meta?.totalItems ?? 0 }}
          onPageChange={setPage}
          emptyState={
            <EmptyState
              title='No documents found'
              description='Upload a PDF and Wundo will read it for this organisation.'
            />
          }
          toolbar={
            <>
              {/*
                Hidden, and opened by the button beside it: the native control
                cannot be styled, and "No file chosen" says nothing next to a
                table that already lists what is there.
              */}
              <Input
                ref={field}
                type='file'
                accept='.pdf'
                multiple
                className='hidden'
                aria-label='Choose PDF files'
                onChange={(event) => {
                  upload.choose(event.target.files);
                  event.target.value = '';
                }}
              />
              <Button
                size='sm'
                loading={upload.uploading}
                disabled={upload.uploading}
                onClick={() => field.current?.click()}
              >
                {upload.uploading ? 'Uploading…' : 'Upload'}
              </Button>
            </>
          }
          actions={(row) => (
            <TableActionsMenu
              label={`Actions for ${row.file_name}`}
              items={[
                {
                  label: 'View chunks',
                  icon: <Eye className='size-4' aria-hidden='true' />,
                  /* Nothing to show until the document has been read and split. */
                  disabled: row.status !== 'completed',
                  onSelect: () => setViewing(row),
                },
                {
                  label: 'Download',
                  icon: <Download className='size-4' aria-hidden='true' />,
                  onSelect: () => void download(row),
                },
                {
                  label: 'Delete',
                  icon: <Trash2 className='size-4' aria-hidden='true' />,
                  tone: 'danger',
                  onSelect: () => setDeleting(row),
                },
              ]}
            />
          )}
        />
      </PageBody>

      <DocumentChunksDialog document={viewing} onClose={() => setViewing(null)} />

      <DeleteDocumentDialog document={deleting} onClose={() => setDeleting(null)} />
    </>
  );
}

const PROCESSING_POLL_MS = 5000;

function useDocumentList(organisationId: string | undefined) {
  /*
   * Which page of which organisation's documents. The two are one answer:
   * switching organisation is a different list, not page four of this one, and
   * page four of the last organisation may not exist in the new one.
   */
  const [paging, setPaging] = useState({ organisationId, page: 1 });
  const page = paging.organisationId === organisationId ? paging.page : 1;
  const [sort, setSort] = useState<DataTableSort>({
    key: 'created_at',
    direction: 'desc',
  });

  function setPage(next: number) {
    setPaging({ organisationId, page: next });
  }

  function changeSort(next: DataTableSort) {
    setSort(next);
    setPage(1);
  }

  const documents = useQuery({
    queryKey: ['documents', { organisationId, page, sort }],
    queryFn: () =>
      listDocuments({
        page,
        limit: PAGE_SIZE,
        organisationId,
        sortBy: sort.key,
        sortOrder: sort.direction,
      }),
    /* A document is embedded in the background: keep asking until nothing is moving. */
    refetchInterval: (query) =>
      query.state.data?.data.some(
        (item) => item.status === 'pending' || item.status === 'processing',
      )
        ? PROCESSING_POLL_MS
        : false,
    /* The rows stay put between pages and polls; only the numbers change. */
    placeholderData: (previous) => previous,
  });

  return { documents, page, setPage, sort, changeSort };
}

/** The only thing the documents page will take. The backend reads the bytes itself. */
const ACCEPTED_MIME_TYPE = 'application/pdf';

/**
 * Uploading PDFs one at a time, on purpose: the queue above the table is only
 * readable because exactly one upload is in flight, and the backend embeds
 * each document as it arrives.
 */
function useDocumentUpload(organisationId: string | undefined) {
  const queryClient = useQueryClient();
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [error, setError] = useState('');

  function markUpload(index: number, patch: Pick<UploadItem, 'status' | 'error'>) {
    setUploads((current) =>
      current.map((entry, at) => (at === index ? { ...entry, ...patch } : entry)),
    );
  }

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      setUploads(files.map((file) => ({ name: file.name, status: 'pending' })));

      for (const [index, file] of files.entries()) {
        markUpload(index, { status: 'uploading' });

        try {
          await uploadDocument(file, organisationId);
          markUpload(index, { status: 'success' });
        } catch (failure) {
          reportError(failure, { where: 'documents.upload', fileName: file.name });
          markUpload(index, { status: 'error', error: 'Failed to upload file' });
        }
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['documents'] });
      /* The final status is the point of the panel — leave it up long enough to read. */
      window.setTimeout(() => setUploads([]), 2000);
    },
  });

  function choose(files: FileList | null) {
    const chosen = Array.from(files ?? []);
    const accepted = chosen.filter((file) => file.type === ACCEPTED_MIME_TYPE);
    const refused = chosen.filter((file) => file.type !== ACCEPTED_MIME_TYPE);

    setError(
      refused.length > 0
        ? `Only PDF files are allowed. Skipped: ${refused.map((file) => file.name).join(', ')}`
        : '',
    );

    if (accepted.length > 0) {
      upload.mutate(accepted);
    }
  }

  return { uploads, error, uploading: upload.isPending, choose };
}

/*
 * The dialog stays open on a failure and says so inside itself: closing it
 * would take the only report of what went wrong with it.
 */
function DeleteDocumentDialog({
  document,
  onClose,
}: {
  document: Document | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');

  const remove = useMutation({
    mutationFn: (row: Document) => deleteDocument(row.id),
  });

  async function confirm() {
    if (!document) return;

    setError('');
    try {
      await remove.mutateAsync(document);
      onClose();
      await queryClient.invalidateQueries({ queryKey: ['documents'] });
    } catch (failure) {
      reportError(failure, { where: 'documents.delete', documentId: document.id });
      setError('Failed to delete file');
    }
  }

  return (
    <ConfirmDialog
      open={document !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
          setError('');
        }
      }}
      title='Delete document?'
      description={`Are you sure you want to delete ${document?.file_name}?`}
      confirmLabel='Delete'
      cancelLabel='Cancel'
      variant='danger'
      error={error}
      onConfirm={confirm}
    />
  );
}
